
import { Injectable, inject, signal, Injector, runInInjectionContext } from '@angular/core';
import { Messaging, getToken, onMessage, MessagePayload } from '@angular/fire/messaging';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { DeviceService } from './device.service';

@Injectable({
    providedIn: 'root'
})
export class FcmService {
    private messaging = inject(Messaging);
    private deviceService = inject(DeviceService);
    private injector = inject(Injector);
    private messageSubject = new Subject<MessagePayload>();

    public currentToken = signal<string | null>(null);
    public messages$ = this.messageSubject.asObservable();

    constructor() {
        console.log('FcmService: Constructor initialized');
        this.startListener();
    }

    async requestPermission(): Promise<string | null> {
        try {
            const permission = await Notification.requestPermission();
            if (permission === 'granted') {
                const token = await runInInjectionContext(this.injector, () =>
                    getToken(this.messaging, {
                        vapidKey: environment.firebaseConfig.vapidKey
                    })
                );
                if (token) {
                    this.currentToken.set(token);
                    console.log('FCM Token:', token);
                    return token;
                } else {
                    console.warn('No registration token available. Request permission to generate one.');
                }
            } else {
                console.warn('Unable to get permission to notify.');
            }
        } catch (error) {
            console.error('An error occurred while retrieving token. ', error);
        }
        return null;
    }

    registerCurrentToken(): void {
        const token = this.currentToken();
        if (token) {
            this.deviceService.registerDevice({
                fcmToken: token,
                deviceType: 'WEB'
            }).subscribe({
                next: (resp) => console.log('Device registered successfully:', resp),
                error: (err) => console.error('Error registering device:', err)
            });
        }
    }

    private startListener() {
        console.log('FcmService: Starting listeners...');
        onMessage(this.messaging, (payload) => {
            console.log('FcmService: Message received (Foreground). ', payload);
            this.messageSubject.next(payload);
            this.showForegroundNotification(payload);
        });

        // Listen for messages from service worker (Background broadcast)
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.addEventListener('message', (event) => {
                if (event.data && event.data.type === 'FCM_MSG') {
                    console.log('FcmService: Message received (SW Broadcast). ', event.data.payload);
                    this.messageSubject.next(event.data.payload);
                }
            });
            console.log('FcmService: SW message listener attached.');
        }
    }

    private showForegroundNotification(payload: MessagePayload): void {
        let title = payload.notification?.title;
        let body = payload.notification?.body;
        const icon = (payload.notification as any)?.icon || '/assets/images/loadgate-new-logo.png';

        // If it's a data message without a notification object, try to parse it
        if (!title && payload.data) {
            try {
                const data = payload.data as any;
                const chatMsg = data['message'] ? JSON.parse(data['message']) : data;

                // Handle nested stringified objects in flattened data
                let sender = chatMsg.sender;
                if (typeof sender === 'string') {
                    try { sender = JSON.parse(sender); } catch (e) { /* ignore */ }
                }

                const senderName = sender?.fullName || chatMsg.fullName || data.senderName;
                const text = chatMsg.textContent || data.textContent;

                if (text || senderName) {
                    title = senderName || 'New Message';
                    body = text || 'You have a new message';
                }
            } catch (e) {
                console.warn('Could not parse data for notification', e);
            }
        }

        // Final fallback
        title = title || 'New Message';
        body = body || 'Check your chat for updates';

        if (Notification.permission === 'granted') {
            const notification = new Notification(title, {
                body,
                icon
            });

            notification.onclick = () => {
                window.focus();
                notification.close();
            };
        }
    }
}
